import { Link } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { PageHeader } from "@/components/data-display/page-header";
import { KVList } from "@/components/data-display/kv-list";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipHint, WIP_ACTION_HINT } from "@/components/ui/wip-hint";

/**
 * Audit Export (/evidence/export) — WIP layout port (ALL-5825).
 *
 * Structure, spacing and tokens are ported from the designer's
 * `.design-ref/prototype/features/oversight/evidence.tsx` `AuditExport`.
 * There is no backend for evidence export in this environment
 * (context/page_pipeline/BACKEND_GAPS.md), so — per the port rules — this is
 * a layout-only port:
 *
 *  - no prototype sample data is copied in: every value slot renders the
 *    shared `UnavailableValue` marker and the preview panel renders a designed
 *    empty state;
 *  - every control that would need the export backend is `disabled` and
 *    carries `WIP_ACTION_HINT` through both `title` and `aria-describedby`;
 *  - the only live controls are navigations to routes that exist in App.tsx.
 *
 * It replaced a `DeferredPage` (ALL-5709/ALL-5778): the not-available wording
 * survives as the plain sentence under the title, and the related-pages list
 * it offered is kept verbatim below.
 *
 * Removing the WIP hint is part of the PR that wires the real API.
 */

/** Export formats are designer structure (the picker's options), not business data. */
const EXPORT_FORMATS = ["JSON", "CSV", "PDF"] as const;

const WIP_DESCRIPTION_ID = "evidence-export-wip-description";

/** Panel chrome ported from the designer's `Panel` (features/oversight/oversight-ui.tsx). */
function Panel({
  title,
  children,
  bodyClassName,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-line bg-surface">
      {title && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
          <h3 className="text-sm font-bold text-ink">{title}</h3>
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export default function AuditExportPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: "Audit Export" }]} />

      <PageHeader
        title="Audit Export"
        subtitle="Generate a sealed, tamper-evident bundle of evidence packets for auditors."
        actions={<WipHint />}
      />
      <p className="-mt-3 mb-5 text-sm text-muted">
        Evidence export has no backend in this environment yet, so no export can be produced from this page.
      </p>
      <p id={WIP_DESCRIPTION_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </p>

      <Panel title="Export Configuration" bodyClassName="px-5 py-4">
        <KVList
          rows={[
            { k: "Date range", v: <UnavailableValue /> },
            { k: "Packet types", v: <UnavailableValue /> },
            { k: "Initiated by", v: <UnavailableValue /> },
          ]}
        />

        <div className="mt-5">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.06em] text-muted">Format</div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Export format">
            {EXPORT_FORMATS.map((format) => (
              <Button
                key={format}
                type="button"
                variant="secondary"
                size="sm"
                disabled
                title={WIP_ACTION_HINT}
                aria-describedby={WIP_DESCRIPTION_ID}
              >
                {format}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            type="button"
            disabled
            title={WIP_ACTION_HINT}
            aria-describedby={WIP_DESCRIPTION_ID}
          >
            <Download aria-hidden className="size-4" /> Generate Export
          </Button>
          <Link to="/evidence" className="text-xs font-semibold text-primary hover:underline">
            Cancel
          </Link>
        </div>
      </Panel>

      <Panel title="Export Preview" bodyClassName="px-5 py-4">
        <EmptyState
          icon={<FileText aria-hidden className="size-5 text-muted" />}
          title="No export preview"
          description="Evidence packets are not published in this environment yet, so there is nothing to include in a bundle."
        />
      </Panel>

      <Panel title="Related" bodyClassName="px-5 py-4">
        <ul className="divide-y divide-line">
          <li>
            <Link to="/admin/audit-log" className="block py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Audit Trail</span>
              <span className="block text-xs text-muted">
                Every administrative action, with actor, outcome and reason code.
              </span>
            </Link>
          </li>
          <li>
            <Link to="/admin/trace-lookup" className="block py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Trace lookup</span>
              <span className="block text-xs text-muted">
                Resolve a support reference to the failing service and next step.
              </span>
            </Link>
          </li>
        </ul>
      </Panel>
    </div>
  );
}
